/* eslint-disable @typescript-eslint/no-magic-numbers */
import { Box } from '@cp/ds/src/components/box';
import { Card } from '@cp/ds/src/components/card';
import { Container } from '@cp/ds/src/components/container';
import { Stack } from '@cp/ds/src/components/stack';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import React from 'react';
import { alpha } from '@mui/system/colorManipulator';
import useMediaQuery from '@mui/material/useMediaQuery';
import Grow from '@mui/material/Grow';
import { Typography } from '@cp/ds/src/components/typography';
import { SxProps } from '@cp/ds';
import { useSelector } from 'react-redux';
import { coreCareerApi } from '@cp/entities/career';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { Breadcrumbs } from '@cp/ds/src/components/breadcrumbs';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { CatalogPlaces } from '@common/model/blocks';
import { ResumeFeaturesProvider } from '@cp/entities/resume';
import { LocationFieldset } from '@cp/entities/location';
import { RoutedLink as Link } from 'src/shared/ui/link';
import Header from 'src/widgets/header';
import { H1 } from 'src/app/shared/components/h1';
import { useH1 } from 'src/app/shared/hooks/useH1';
import { CatalogEvents, catalogLayoutDesktopViewModeSelector } from 'src/features/catalog';
import {
  CatalogFeedbacksSection,
  ResumeCardWidgetV2 as CatalogItem,
  CatalogList,
  // YOU-5106: CatalogLocationLinking,
  FAQ,
  FavoriteFeature,
  ResumeCardFallback,
  VerifiedFeature,
  calcDesktopItemListStyles,
  useCatalogBreadcrumbs,
} from 'src/widgets/catalog';
import { CitySelection } from 'src/widgets/location';
import { CatalogFilter } from 'src/entities/catalog/filter';
import { CatalogBannerFreeSearch } from 'src/entities/catalog/banner';
import { useCatalogHeaderConfig } from 'src/app/routes/catalog/hooks/useCatalogHeaderConfig';
import { withTrackClick } from 'src/shared/tracking';
import { PrimaryHeaderBannerV2 } from 'src/widgets/primaryHeaderBanner';
// YOU-5106: import { CatalogCareerTagClouds } from 'src/widgets/catalogCareerTagClouds';
import { RenderRouteBlock } from 'src/features/blocks';
import { CatalogNav } from 'src/entities/catalog/nav';
import { CatalogViewProps } from './types';
import {
  CATALOG_DESKTOP_COLUMN_SPACING,
  CATALOG_DESKTOP_COLUMN_WIDTH_FIXED,
  CATALOG_DESKTOP_CONTAINER_MAX_WIDTH,
  CATALOG_RESUME_SUBTITLE,
} from './const';
import { useFeedbacksParameters } from './hooks/useFeedbacksParameters';
import { LazyCatalogMap } from './lib';

const LIST_MAX_WIDTH = 820;
const FILTER_FIRST_SX: SxProps = { mb: 1.5 };

const CatalogResumeDesktopView: React.FC<CatalogViewProps> = ({
  currentCity,
  pageProps,
  listRequest,
  filter,
  sorting,
  onChangeSorting,
  onChangeCity,
  onChangeFilter,
  onChangeViewMode,
}) => {
  const { catalogType, careerId } = pageProps;
  const theme = useTheme();
  const h1RenderConfig = useH1();
  const { containerSx: itemListContainerSx, cardWidthAutoUntilBreakpoint } = React.useMemo(() => calcDesktopItemListStyles(theme), [theme]);
  const viewMode = useSelector(catalogLayoutDesktopViewModeSelector);
  const isItemCardWidthFull = useMediaQuery(theme.breakpoints.down(cardWidthAutoUntilBreakpoint));
  const { data: careerDictionary } = coreCareerApi.endpoints.getCareers.useQuery();
  const loggedIn = useIsLoggedIn();
  const breadcrumbs = useCatalogBreadcrumbs();
  const feedbacksListParameters = useFeedbacksParameters({ careerId, catalogType, currentCity, careerDictionary });

  const [isFreeSearchBannerVisible, setIsFreeSearchBannerVisible] = React.useState(true);

  const banners = React.useMemo(() => {
    if (!isFreeSearchBannerVisible) {
      return {};
    }

    return {
      9: (
        <Card
          sx={{
            backgroundColor: theme.palette.background.default,
          }}
        >
          <CatalogBannerFreeSearch onClick={() => setIsFreeSearchBannerVisible(false)} />
        </Card>
      ),
      // 9: (
      //   <Card
      //     sx={{ borderWidth: 2, borderColor: theme.palette['brand pink'], backgroundColor: theme.palette.background.default }}
      //   >
      //     <CatalogBannerNoDoubts careerDict={careerDictionary} careerId={pageProps.careerId} />
      //   </Card>
      // ),
    } as const;
  }, [isFreeSearchBannerVisible, theme.palette.background.default]);

  const renderSection = ({
    content,
    filled,
    limitWidth,
    title,
    href,
  }: {
    href?: string;
    filled?: boolean;
    content: React.ReactNode;
    limitWidth?: boolean;
    title: React.ReactNode;
  }) => {
    const header = <Typography sx={{ mb: 5, typography: { xs: 'h5', md: 'h1' } }}>{title}</Typography>;

    return (
      <Box
        sx={{
          mt: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          ...(limitWidth && {
            maxWidth: LIST_MAX_WIDTH,
            width: 1,
            mx: 'auto',
          }),
          ...(filled && {
            px: 3.75,
            pt: 1.5,
            pb: 3.75,
            borderRadius: `${theme.borderRadius.extraLarge}px`,
            backgroundColor: alpha(theme.palette['gray middle'], 0.2),
          }),
        }}
      >
        {href ? (
          <Link external href={href} sx={{ mb: 5 }} underline={false}>
            {header}
          </Link>
        ) : (
          header
        )}
        {content}
      </Box>
    );
  };

  return (
    <Box component="main" sx={{ backgroundColor: 'background.paper' }}>
      <Header
        {...useCatalogHeaderConfig()}
        containerProps={{ maxWidth: CATALOG_DESKTOP_CONTAINER_MAX_WIDTH }}
        sx={{
          boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.1)',
          zIndex: 10,
          ...(viewMode === 'map'
            ? { position: 'fixed', left: 0, top: 0, right: 0, width: 1 }
            : {
                position: 'sticky',
                top: 0,
                left: 0,
                right: 0,
              }),
          backgroundColor: theme.palette.white,
        }}
      />

      <Container maxWidth={CATALOG_DESKTOP_CONTAINER_MAX_WIDTH}>
        <Box sx={{ overflow: 'hidden', my: 2.5 }}>
          <PrimaryHeaderBannerV2
            content={
              <H1
                config={h1RenderConfig}
                itemProps={{ bold: { variant: 'h2' } }}
                rootProps={{
                  sx: {
                    transition: theme.transitions.create(['max-height'], {
                      duration: theme.transitions.duration.standard,
                    }),
                    overflow: 'hidden',
                    ml: 0,
                    mr: 'auto',
                    mt: 1,
                    mb: 0,
                  },
                  variant: 'h2',
                }}
              />
            }
            header={<Breadcrumbs LinkComponent={Link} items={breadcrumbs} />}
            loggedIn={loggedIn}
            pageProps={pageProps}
            subtitleLight={loggedIn ? '' : CATALOG_RESUME_SUBTITLE}
            text={loggedIn}
          />
        </Box>

        <Box sx={{ mb: 14, display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
          <Box
            sx={{
              width: CATALOG_DESKTOP_COLUMN_WIDTH_FIXED,
              flexShrink: 0,
              backgroundColor: 'white',
              marginRight: CATALOG_DESKTOP_COLUMN_SPACING,
              p: 1.5,
              borderRadius: '16px',
              boxShadow: '14px 17px 40px 4px rgba(138, 182, 209, 0.08)',
            }}
          >
            <CatalogNav
              careerDictionary={careerDictionary}
              careerId={pageProps.careerId}
              catalogType={pageProps.catalogType}
              citySearchFormField={
                <LocationFieldset
                  // endAdornment={<LocationIcon fontSize="1rem" />}
                  placeholder="Город, адрес"
                  size="small"
                  sx={{ width: '100%', zIndex: 4 }}
                />
              }
              defaultCity={currentCity}
              defaultFilter={filter}
              defaultSorting={sorting}
              onChange={onChangeFilter}
              onChangeCity={onChangeCity}
              onChangeSorting={onChangeSorting}
              sx={FILTER_FIRST_SX}
            />
            <CatalogFilter
              careerDictionary={careerDictionary}
              careerId={pageProps.careerId}
              catalogType={pageProps.catalogType}
              defaultFilter={filter}
              onChange={onChangeFilter}
              onMapButtonClick={viewMode !== 'map' ? () => onChangeViewMode('map') : undefined}
            />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Grow appear={false} in={viewMode === 'map'} unmountOnExit>
              <Box
                sx={{
                  width: '100%',
                  height: 480,
                  overflow: 'hidden',
                  borderRadius: '20px',
                  mb: CATALOG_DESKTOP_COLUMN_SPACING,
                }}
              >
                <LazyCatalogMap
                  listRequest={listRequest}
                  onClose={() => onChangeViewMode('list')}
                  sx={{ width: 1, height: 1, flexGrow: 1, position: 'relative', top: 0, left: 0, right: 0, bottom: 0 }}
                  zoomControl
                />
              </Box>
            </Grow>

            <Box sx={itemListContainerSx}>
              <ResumeFeaturesProvider
                value={{
                  sidebar: {
                    right: [FavoriteFeature, VerifiedFeature],
                  },
                }}
              >
                <CatalogList
                  CatalogItem={withTrackClick(CatalogItem, CatalogEvents.ItemClick)}
                  FallbackCard={ResumeCardFallback}
                  banners={banners}
                  itemSpacing={CATALOG_DESKTOP_COLUMN_SPACING}
                  itemWidth={isItemCardWidthFull ? '100%' : CATALOG_DESKTOP_COLUMN_WIDTH_FIXED}
                  request={listRequest}
                  sx={{ justifyContent: 'flex-start' }}
                />
              </ResumeFeaturesProvider>
            </Box>
          </Box>
        </Box>

        {!loggedIn && <RenderRouteBlock blockName={CatalogPlaces.infoContent} />}

        {catalogType === CatalogItemType.Resume && (
          <Box sx={{ mt: 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <CatalogFeedbacksSection
              {...feedbacksListParameters}
              sectionTitle={
                <Typography sx={{ mb: 5, typography: { xs: 'h5', md: 'h1' } }}>
                  Отзывы о {careerDictionary?.[careerId].translation.namePluralPrepositional} в{' '}
                  {currentCity.translation.namePrepositional}
                </Typography>
              }
            />
          </Box>
        )}

        {renderSection({
          title: 'Часто задаваемые вопросы',
          /* eslint-disable-next-line react/jsx-pascal-case */
          content: <FAQ listRequest={listRequest} />,
        })}

        {/* YOU-5106: restore when other specialties are enabled
        {renderSection({
          title: 'Вас может заинтересовать',

          content: (
            <Stack
              direction="row"
              spacing={2.5}
              sx={{ alignItems: 'flex-start', width: '100%', pb: 10, '& > *': { flexBasis: 'calc(50% - 10px)' }, flexWrap: 'wrap' }}
            >
              <CatalogCareerTagClouds />
              <CatalogLocationLinking sx={{ '#root &': { mt: 2.5, ml: 0 } }} />
            </Stack>
          ),
        })}
        */}
      </Container>

      {/* City selection modal */}
      <CitySelection />
    </Box>
  );
};

export default React.memo(CatalogResumeDesktopView);
